import React, { useState } from 'react';
import { Box, Chip, CircularProgress, Collapse, Link, Paper, Tooltip, Typography } from '@mui/material';
import {
  Description as DocIcon,
  Visibility as ViewIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateFormat'; // dd/mm/yyyy on every browser

export const prettyDocType = (t) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Thaklis makes a NEW group per re-upload (old ones flagged isCurrent=false), so "versions" of one
// document are spread across groups of the same type. Merge them into one entry per type/title,
// newest version first, so the UI shows a single current doc with a collapsible history.
export const mergeDocGroupsByType = (groups) => {
  const map = new Map();
  (groups || []).forEach((g) => {
    const common = g?.common || {};
    const typeKey = `${common.documentType || ''}|${common.title || ''}`;
    if (!map.has(typeKey)) map.set(typeKey, { typeKey, headerCommon: common, versions: [] });
    const entry = map.get(typeKey);
    if (common.isCurrent) entry.headerCommon = common; // prefer the current group's metadata
    // Carry each version's own group metadata (issue/expiry/number can differ across versions,
    // e.g. a renewed visa) so the history rows can show it.
    (Array.isArray(g.documents) ? g.documents : []).forEach((d) =>
      entry.versions.push({ ...d, groupCommon: common, groupId: g.documentGroupId }));
  });
  const out = Array.from(map.values());
  out.forEach((e) => e.versions.sort((a, b) =>
    (b.version || 0) - (a.version || 0) || (new Date(b.uploadDate || 0) - new Date(a.uploadDate || 0))));
  return out;
};

// Who uploaded a version (PORTAL-EMPLOYEES-AND-FEATURES.md task PE3). Shown only once the backend sends it.
const UploaderChip = ({ doc }) => {
  const source = doc.uploadedBySource || doc.groupCommon?.uploadedBySource;
  if (!source) return null;
  const byClient = source === 'PORTAL';
  return (
    <Tooltip title={doc.uploadedByName || ''} disableHoverListener={!doc.uploadedByName}>
      <Chip
        size="small"
        label={byClient ? 'By client' : 'By typing center'}
        color={byClient ? 'info' : 'default'}
        sx={{ height: 18, fontSize: '0.65rem' }}
      />
    </Tooltip>
  );
};

/**
 * An employee's documents as cards, one per document type, newest version first with the history
 * behind a toggle. Used by the typing center (client › Employees) and the client portal (Employees).
 *
 * Props:
 *  - uid, groups            grouped-documents response (`groups`)
 *  - onView(filePath, key)  open a file; omit to hide View
 *  - viewingKey             key of the file being opened (spinner)
 *  - onDelete(target)       typing center only: { uid, groupId, label, fileCount }
 */
const EmployeeDocuments = ({ uid, groups, onView, viewingKey = null, onDelete }) => {
  const [openVersions, setOpenVersions] = useState({});
  const entries = mergeDocGroupsByType(groups);

  const renderDelete = (d, entry) => {
    if (!onDelete || !d.groupId) return null;
    const common = entry.headerCommon || {};
    const docName = `${prettyDocType(common.documentType)}${common.title ? ` — ${common.title}` : ''}`;
    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        color="error"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
        onClick={() => onDelete({
          uid,
          groupId: d.groupId,
          label: `Version ${d.version ?? '—'} of ${docName}`,
          fileCount: entry.versions.filter((v) => v.groupId === d.groupId).length
        })}
      >
        <DeleteIcon sx={{ fontSize: 15 }} />
        Delete
      </Link>
    );
  };

  // One compact line for a file: filename · date · View, plus Delete when the version has a single file.
  const renderFileRow = (d, entry, idx, showDelete, divider = false) => {
    const key = d.id || `${uid}-${entry.typeKey}-${d.version}-${idx}`;
    return (
      <Box key={key} sx={{ py: 0.5, ...(divider ? { borderBottom: '1px solid', borderColor: 'divider' } : {}) }}>
        <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', wordBreak: 'break-word' }}>{d.fileName || 'file'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {d.uploadDate && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploaded {formatDate(d.uploadDate)}</Typography>
        )}
        <UploaderChip doc={d} />
        {d.filePath && onView && (
          <Link
            component="button"
            type="button"
            underline="hover"
            disabled={viewingKey === key}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
            onClick={() => onView(d.filePath, key)}
          >
            {viewingKey === key ? <CircularProgress size={12} /> : <ViewIcon sx={{ fontSize: 15 }} />}
            View
          </Link>
        )}
        {showDelete && renderDelete(d, entry)}
        </Box>
      </Box>
    );
  };

  // All files of one version: the current one is plain rows, a previous one a mini-card with its label and dates.
  const renderGroup = (files, entry, idxBase, versionLabel) => {
    const single = files.length === 1;
    const rows = files.map((d, i) => renderFileRow(d, entry, idxBase + i, single, !single && i < files.length - 1));
    const deleteAll = !single && <Box sx={{ mt: 0.75 }}>{renderDelete(files[0], entry)}</Box>;
    if (!versionLabel) {
      return <Box key={`cur-${files[0].groupId || files[0].id}`} sx={{ py: 0.25 }}>{rows}{deleteAll}</Box>;
    }
    const vc = files[0].groupCommon || {};
    const meta = [
      vc.issueDate ? `Issued ${formatDate(vc.issueDate)}` : null,
      vc.expiryDate ? `Expires ${formatDate(vc.expiryDate)}` : null,
    ].filter(Boolean).join('  ·  ');
    return (
      <Box key={`prev-${files[0].groupId || files[0].id}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
          {versionLabel}
        </Typography>
        {meta && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>{meta}</Typography>
        )}
        {rows}
        {deleteAll}
      </Box>
    );
  };

  const renderDocType = (entry) => {
    const common = entry.headerCommon || {};
    const groupKey = (d) => d.groupId || d.id;
    const currentFiles = entry.versions.filter((v) => groupKey(v) === groupKey(entry.versions[0]));
    const previousGroups = [];
    entry.versions.slice(currentFiles.length).forEach((d) => {
      const last = previousGroups[previousGroups.length - 1];
      if (last && groupKey(last[0]) === groupKey(d)) last.push(d); else previousGroups.push([d]);
    });
    const previous = previousGroups;
    const vkey = entry.typeKey;
    const showPrev = !!openVersions[vkey];
    const metaLine = [
      common.issueDate ? `Issued ${formatDate(common.issueDate)}` : null,
      common.expiryDate ? `Expires ${formatDate(common.expiryDate)}` : null,
    ].filter(Boolean).join('  ·  ');

    return (
      <Paper key={entry.typeKey} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DocIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {prettyDocType(common.documentType)}{common.title ? ` — ${common.title}` : ''}
          </Typography>
          <Chip label="Current" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        {metaLine && (
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 3.25, display: 'block' }}>
            {metaLine}
          </Typography>
        )}
        <Box sx={{ ml: 3.25, mt: 0.25 }}>
          {currentFiles.length > 0 && renderGroup(currentFiles, entry, 0)}
          {previous.length > 0 && (
            <>
              <Link
                component="button"
                type="button"
                underline="hover"
                sx={{ fontSize: '0.72rem', mt: 0.25 }}
                onClick={() => setOpenVersions((p) => ({ ...p, [vkey]: !p[vkey] }))}
              >
                {showPrev ? 'Hide' : 'Show'} {previous.length} previous version{previous.length > 1 ? 's' : ''}
              </Link>
              <Collapse in={showPrev} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 0.5 }}>
                  {previous.map((files, idx) => renderGroup(files, entry, idx + 1, `Version ${files[0].version ?? '—'}`))}
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{
      display: 'grid',
      gap: 1.5,
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 360px))',
      alignItems: 'stretch'
    }}>
      {entries.map(renderDocType)}
    </Box>
  );
};

export default EmployeeDocuments;
